import { Loader2 } from "lucide-react";
import { FormEvent, useState } from "react";

import type { ApiKeyScope } from "@/api/generated/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SCOPE_GROUPS, type ScopeGroup } from "@/hooks/use-api-key-settings";

function getTomorrowDateInputValue(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const day = String(tomorrow.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

interface ScopeGroupRowProps {
  group: ScopeGroup;
  selectedScopes: ApiKeyScope[];
  toggleReadScopes: (group: ScopeGroup) => void;
  toggleWriteScopes: (group: ScopeGroup) => void;
}

function ScopeGroupRow({ group, selectedScopes, toggleReadScopes, toggleWriteScopes }: ScopeGroupRowProps) {
  const readScopes = group.readScopes ?? [group.read];
  const writeScopes = group.writeScopes ?? (group.write ? [group.write] : []);

  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <span className="min-w-0 truncate text-sm">{group.label}</span>
      <div className="flex shrink-0 items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Checkbox
            aria-label={`${group.label} read`}
            checked={readScopes.every((scope) => selectedScopes.includes(scope))}
            onCheckedChange={() => toggleReadScopes(group)}
          />
          {group.readLabel ?? "Read"}
        </label>
        {writeScopes.length > 0 && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox
              aria-label={`${group.label} write`}
              checked={writeScopes.every((scope) => selectedScopes.includes(scope))}
              onCheckedChange={() => toggleWriteScopes(group)}
            />
            Write
          </label>
        )}
      </div>
    </div>
  );
}

interface ApiKeyCreateFormProps {
  name: string;
  setName: (name: string) => void;
  selectedScopes: ApiKeyScope[];
  toggleReadScopes: (group: ScopeGroup) => void;
  toggleWriteScopes: (group: ScopeGroup) => void;
  expiresAt: string;
  setExpiresAt: (value: string) => void;
  createDisabled: boolean;
  isCreating: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export function ApiKeyCreateForm({
  name,
  setName,
  selectedScopes,
  toggleReadScopes,
  toggleWriteScopes,
  expiresAt,
  setExpiresAt,
  createDisabled,
  isCreating,
  onSubmit,
}: ApiKeyCreateFormProps) {
  const [isScopeCustomizationOpen, setIsScopeCustomizationOpen] = useState(false);
  const [isExpiryOpen, setIsExpiryOpen] = useState(false);

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="space-y-2">
        <Label htmlFor="api-key-name">Key name</Label>
        <Input
          id="api-key-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={120}
          placeholder="Zapier integration"
          required
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label>Access</Label>
            <p className="text-xs text-muted-foreground">Read and write access by default — customize scopes as needed</p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsScopeCustomizationOpen((open) => !open)}
              aria-expanded={isScopeCustomizationOpen}
            >
              {isScopeCustomizationOpen ? "Hide scopes" : "Customize scopes"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsExpiryOpen((open) => !open)}
              aria-expanded={isExpiryOpen}
            >
              {isExpiryOpen ? "Hide expiry" : "Set expiry"}
            </Button>
          </div>
        </div>
        {isScopeCustomizationOpen && (
          <div
            className="grid gap-x-8 gap-y-3 sm:grid-flow-col sm:grid-cols-2"
            style={{ gridTemplateRows: `repeat(${Math.ceil(SCOPE_GROUPS.length / 2)}, auto)` }}
          >
            {SCOPE_GROUPS.map((group) => (
              <ScopeGroupRow
                key={group.label}
                group={group}
                selectedScopes={selectedScopes}
                toggleReadScopes={toggleReadScopes}
                toggleWriteScopes={toggleWriteScopes}
              />
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Billing and running comparisons/supplements are not available via API keys.
        </p>
      </div>

      {isExpiryOpen && (
        <div className="space-y-2">
          <Label htmlFor="api-key-expiry">Expiry date</Label>
          <Input
            id="api-key-expiry"
            type="date"
            value={expiresAt}
            min={getTomorrowDateInputValue()}
            onChange={(event) => setExpiresAt(event.target.value)}
            className="max-w-xs"
          />
          <p className="text-xs text-muted-foreground">
            The key stops working at the end of this day.
          </p>
        </div>
      )}

      <Button type="submit" disabled={createDisabled} className="max-sm:w-full">
        {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Create API key
      </Button>
    </form>
  );
}
